import z from "zod";

import { SIZES } from "@frontend/styles/constants";

// Absent -> false (e.g. `disabled`, `free-text`).
const booleanAttribute = z
  .string()
  .optional()
  .transform((value) => value !== undefined && value !== "false");

// Absent -> true (e.g. `allow-clear`, which selector.js also defaults on).
const booleanAttributeDefaultTrue = z
  .string()
  .optional()
  .transform((value) => value === undefined || value !== "false");

const comboboxOptionSchema = z
  .object({
    id: z.string(),
    text: z.string(),
    description: z
      .string()
      .optional()
      .describe(
        "Secondary line rendered under the option label, read from the item's data-description attribute or passed to update().",
      ),
    status: z
      .enum(["success", "warning", "critical"])
      .optional()
      .describe(
        "Colored dot rendered before the option label, passed to update().",
      ),
    group: z
      .string()
      .optional()
      .describe(
        "Label of the enclosing <rwc-combobox-group>, if the option is grouped.",
      ),
  })
  .catchall(z.unknown());

export type ComboboxOption = z.infer<typeof comboboxOptionSchema>;

export const comboboxDataSchema = z.object({
  items: z
    .array(comboboxOptionSchema)
    .default([])
    .describe(
      "Options parsed from the light DOM (<select><option> elements inside <rwc-combobox>) — not a Twig-authored attribute.",
    ),
  defaultValue: z
    .string()
    .nullable()
    .optional()
    .describe(
      "Initially selected option id, read from data-initial-id or the <select>'s selected <option> — not settable after mount, use el.select(id) instead.",
    ),
  label: z.string().optional(),
  size: z.enum(SIZES).optional(),
  placeholder: z.string().optional(),
  searchPlaceholder: z.string().optional(),
  noResultsText: z.string().optional(),
  popupWidth: z
    .string()
    .optional()
    .describe(
      'Popup width: "anchor" (default, matches the trigger), "content" (widest option, capped by the space left on screen), or any CSS length such as "32em". Never narrower than the trigger. "content" cannot work with virtualized, which renders its options out of flow.',
    ),
  clearable: booleanAttributeDefaultTrue,
  inputInsidePopup: booleanAttributeDefaultTrue,
  freeText: booleanAttribute,
  disabled: booleanAttribute,
  required: booleanAttribute,
  virtualized: booleanAttribute,
  name: z.string().optional(),
  onValueChange: z
    .custom<(id: string | null, item: ComboboxOption | null) => void>()
    .optional()
    .describe(
      "Internal callback bound by readComboboxSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
  onSelect: z
    .custom<(item: ComboboxOption) => void>()
    .optional()
    .describe(
      "Internal callback bound by readComboboxSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type ComboboxDataProps = z.infer<typeof comboboxDataSchema>;
